/**
 * Карточка записи справочника культур — состояния 3, 4 и 8 макета.
 *
 * UAT founder 08.08 дословно: «я тут изменить культуру не могу, ни цвета
 * поменять, нихрена». Раньше список был read-only: строка ничего не открывала.
 * Теперь клик по строке открывает эту панель, и в ней ровно то, что нарисовано
 * в определяющем макете, — ни поля больше:
 *
 *  · как называется у нас (своё название, на эталон не влияет);
 *  · цвет: системный ↔ свой + «вернуть системный» (AK-548) — это легенда карты;
 *  · в системе это (эталон) — ФАКТ; менять можно, только пока по записи нет
 *    посевов, иначе они уехали бы не на ту культуру;
 *  · где видна: весь холдинг / кластер / хозяйство (С-3);
 *  · как используется — числа без ссылок (контекст неоднозначен);
 *  · в архив / вернуть из архива (С-4 / С-5).
 *
 * Чего в макете нет — здесь не выдумано: истории изменений, сортов и слияния
 * дублей в этой панели нет, они отдельными стори.
 */
import { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import { cropLabel } from '@/lib/cropLabel';
import { formatRuDate } from '@/lib/dateFmt';
import {
  CATALOG_COLOR_SWATCHES,
  CATALOG_NEUTRAL_SWATCHES,
  DEFAULT_CROP_COLOR,
  NO_COLOR_SWATCH,
} from '@/lib/colorMaps';
import { ColorSwatchPicker } from '@/components/ui/color-swatch-picker';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AvailableScopes } from '@/components/scope/ScopeTree';
import { ScopeMembershipPicker } from './ScopeMembershipPicker';
import {
  useArchiveCropRecord,
  useCanonSearch,
  useCropRecordUsage,
  usePatchCropRecord,
  useRestoreCropRecord,
  useRevertNotACrop,
  type CropQueueItem,
  type CropRecordPatch,
  type CropScope,
} from '@/lib/reference/tenantCrops';
import { useCan } from '@/lib/auth/usePermissions';
import { plural, typeBadgeLabel } from './catalog-review/reviewLabels';
import { NotACropDialog, SUMMARIES_LIMIT_TEXT } from './NotACropDialog';

/** Палитра карточки: насыщенные + нейтральные одной сеткой. */
const CATALOG_SWATCHES = [...CATALOG_COLOR_SWATCHES, ...CATALOG_NEUTRAL_SWATCHES];

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.detail : fallback;
}

/**
 * Области записи: то, что пришло с сервера, либо одна область из первичной
 * пары — запись, заведённая до AK-570, строк членства ещё не имеет.
 */
function scopesOf(item: CropQueueItem): CropScope[] {
  if (item.scopes?.length) return item.scopes;
  return [
    {
      scope_level: item.scope_level ?? 'holding',
      scope_ref_id: item.scope_ref_id ?? null,
      name: item.scope_name ?? null,
    },
  ];
}

/** Сравнение наборов областей без учёта порядка — для признака «правили». */
function scopesKey(scopes: CropScope[]): string {
  return scopes
    .map((s) => `${s.scope_level}:${s.scope_ref_id ?? ''}`)
    .sort()
    .join(',');
}

export function CatalogRecordSheet({
  item,
  open,
  onOpenChange,
  onArchiveAndCreate,
}: {
  item: CropQueueItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Выход из гарда перепривязки: заархивировать эту запись и сразу открыть
   * форму создания новой. Без него гард был тупиком — «заархивируйте и
   * добавьте новую» без единой кнопки (расхождение 13 спеки унификации).
   */
  onArchiveAndCreate?: (item: CropQueueItem) => void;
}) {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [scopes, setScopes] = useState<CropScope[]>([]);
  const [relinkQuery, setRelinkQuery] = useState('');
  const [notACropOpen, setNotACropOpen] = useState(false);

  const usage = useCropRecordUsage(open && item ? item.id : null);
  const patch = usePatchCropRecord();
  const archive = useArchiveCropRecord();
  const restore = useRestoreCropRecord();
  const relink = useCanonSearch(relinkQuery);
  const revertNotACrop = useRevertNotACrop();
  const canEditCatalog = useCan('company_reference', 'u');
  // Право то же, что у остальной правки справочника тенанта (`CatalogSection`
  // гейтит им плашку Р-2): «не культура» — это правка ЗАПИСИ, а не разбор
  // порции интеграции. Нового ресурса не заводим (RBAC-каталог — ADR-040).
  // Дерево «холдинг → кластеры → хозяйства» — та же ручка, что кормит
  // навигатор и фильтр аналитики (#716): второй источник правды о структуре
  // холдинга заводить незачем.
  const scopeTree = useQuery({
    queryKey: ['scope', 'available'],
    queryFn: () => api.get<AvailableScopes>('/api/v1/scope/available'),
    staleTime: 5 * 60_000,
    enabled: open,
  });

  useEffect(() => {
    if (!open || !item) return;
    setName(item.local_name ?? '');
    setColor(item.color_override ?? null);
    setScopes(scopesOf(item));
    setRelinkQuery('');
  }, [open, item]);

  if (!item) return null;

  const archived = Boolean(item.archived_at);
  const used = usage.data?.sowings ?? item.field_count;
  const planned = usage.data?.planned_sowings ?? 0;
  const growing = usage.data?.growing_sowings ?? 0;
  const busy =
    patch.isPending || archive.isPending || restore.isPending || revertNotACrop.isPending;
  const notACrop = Boolean(item.not_a_crop_kind);
  const isMix = item.not_a_crop_kind === 'crop_mix' || (item.components_count ?? 0) > 0;

  const dirty =
    name.trim() !== (item.local_name ?? '') ||
    (color ?? null) !== (item.color_override ?? null) ||
    scopesKey(scopes) !== scopesKey(scopesOf(item));

  function save(extra?: CropRecordPatch) {
    if (!item) return;
    const body: CropRecordPatch = { ...extra };
    if (name.trim() !== (item.local_name ?? '')) body.local_name = name.trim();
    if ((color ?? null) !== (item.color_override ?? null)) body.color_override = color;
    if (scopesKey(scopes) !== scopesKey(scopesOf(item))) {
      body.scopes = scopes.map((sc) => ({
        scope_level: sc.scope_level,
        scope_ref_id: sc.scope_ref_id,
      }));
    }
    patch.mutate(
      { itemId: item.id, patch: body },
      {
        onSuccess: () => {
          toast.success('Сохранено');
          onOpenChange(false);
        },
        onError: (e) => toast.error(errMsg(e, 'Не удалось сохранить')),
      },
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            <span
              aria-hidden
              className="inline-block h-4 w-4 shrink-0 rounded-sm border border-border"
              style={
                (color ?? item.crop_color)
                  ? { backgroundColor: color ?? item.crop_color ?? undefined }
                  : undefined
              }
            />
            {item.local_name ?? '—'}
            <Badge variant="outline">
              {typeBadgeLabel(
                item.not_a_crop_kind ?? null,
                (item.components_count ?? 0) > 0,
              )}
            </Badge>
            {archived && <Badge variant="secondary">в архиве</Badge>}
          </SheetTitle>
          <SheetDescription>
            {archived
              ? // Провенанс слияния (AK-569): «в архиве» без «куда слили» — это
                // ровно то состояние, из-за которого в AK-562 пришлось
                // восстанавливать связи по косвенным признакам.
                `В архиве с ${formatRuDate(item.archived_at as string)}${
                  item.archived_by_name ? ` · ${item.archived_by_name}` : ''
                }${
                  item.merged_into_name ? ` · слита в «${item.merged_into_name}»` : ''
                }`
              : 'Как эта запись называется у вас, каким цветом рисуется и где её видно.'}
          </SheetDescription>
        </SheetHeader>

        {/* Название */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="rec-name">Как называется у нас</Label>
          <Input
            id="rec-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground">
            Название своё — можно сократить под привычку хозяйства. На то, чем
            запись является в системе, это не влияет.
          </p>
        </div>

        {/* Цвет — AK-548. Сетка пресетов вместо RGB-пикера (решение founder). */}
        <div className="flex flex-col gap-2">
          {/* `htmlFor` на группу свотчей не работает (это не форменный
              контрол) — связываем через aria-labelledby (ревью head-web).
              Поэтому здесь обычный span со стилем метки, а не <Label>: пустой
              <label> без цели — мусор для скринридера. */}
          <span
            id="rec-color-label"
            className="text-sm font-medium leading-none text-foreground"
          >
            Цвет на карте и в отчётах
          </span>
          <ColorSwatchPicker
            labelledBy="rec-color-label"
            value={color}
            onChange={setColor}
            swatches={CATALOG_SWATCHES}
            noColorValue={NO_COLOR_SWATCH}
            onReset={() => setColor(null)}
            inheritedColor={item.system_color ?? DEFAULT_CROP_COLOR}
            disabled={busy}
          />
          <p className="text-sm text-muted-foreground">
            {color
              ? `свой цвет ${color}`
              : item.system_color
                ? `из общего справочника ${item.system_color}`
                : 'цвет не задан'}
          </p>
          <p className="text-xs text-muted-foreground">
            Этим цветом поле заливается на карте и рисуется в легендах. Свой цвет
            остаётся вашим: если цвет поменяют в общем справочнике, ваш не собьётся.
            Карта подхватит новый цвет в ближайшем обновлении.
          </p>
        </div>

        {/* Эталон — факт + гард перепривязки */}
        <div className="flex flex-col gap-2">
          <Label>В системе это</Label>
          <p className="text-sm text-foreground">
            {item.field_state_name ??
              item.land_category_name ??
              item.canonical_name ??
              'своя запись без привязки'}
          </p>
          {used > 0 ? (
            <Alert>
              <AlertDescription className="flex flex-col items-start gap-2">
                <span>
                  Запись используется в {used} посевах, поэтому привязку менять
                  нельзя — посевы уехали бы не на ту культуру. Нужна другая
                  культура: заархивируйте эту запись и добавьте новую.
                </span>
                {onArchiveAndCreate && !archived && planned === 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => onArchiveAndCreate(item)}
                  >
                    Заархивировать и создать новое
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Привязать к другой культуре — начните вводить название…"
                  value={relinkQuery}
                  onChange={(e) => setRelinkQuery(e.target.value)}
                  disabled={busy}
                />
              </div>
              {relinkQuery.trim().length >= 2 && (
                <div className="max-h-40 overflow-y-auto rounded-lg border border-border">
                  {(relink.data ?? []).length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      Ничего не нашли. Попробуйте другое написание.
                    </p>
                  ) : (
                    <ul>
                      {(relink.data ?? []).map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => save({ ref_id: c.id })}
                            className={cn(
                              'flex min-h-10 w-full items-center px-3 py-2 text-left text-sm',
                              'hover:bg-accent',
                            )}
                          >
                            {cropLabel(c)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Видимость — С-3 */}
        <div className="flex flex-col gap-2">
          {/* Подпись связана с группой через aria-labelledby: `htmlFor` тут
              не работает — пикер не форменный контрол, а группа галочек. */}
          <Label id="rec-scope-label">Где видна</Label>
          <ScopeMembershipPicker
            available={scopeTree.data?.available ?? []}
            value={scopes}
            onChange={setScopes}
            disabled={busy}
            loading={scopeTree.isPending}
            /* Карточка переоткрывается на месте — режим панели обязан
               пересчитаться от НОВОЙ записи, а не остаться от прошлой. */
            resetKey={item.id}
            labelledBy="rec-scope-label"
          />
          <p className="text-xs text-muted-foreground">
            Сужение не переписывает историю: в старых посевах запись остаётся
            видна, её просто перестанут предлагать в новом посеве.
          </p>
        </div>

        {/* «Это не культура» — вход и обратный ход (UAT founder v1.1746).
            Секция стоит СРАЗУ под эталоном: там человек и понимает, что запись
            привязана не к тому классу вещей вообще. Без права на справочник
            секции нет — кнопка, которая всегда отвечает 403, хуже отсутствия.

            У АРХИВНОЙ записи секция есть только если пометка уже стоит: иначе
            это была бы пустая рамка с заголовком и без единого действия. */}
        {canEditCatalog && (notACrop || !archived) && (
          <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
            <Label>Это вообще не культура?</Label>
            {notACrop ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Запись помечена как «не культура» —{' '}
                  {typeBadgeLabel(item.not_a_crop_kind ?? null, isMix)}
                  {item.field_state_name || item.land_category_name
                    ? ` «${item.field_state_name ?? item.land_category_name}»`
                    : ''}
                  .
                </p>
                <p className="text-xs text-muted-foreground">{SUMMARIES_LIMIT_TEXT}</p>
                {archived ? (
                  // Ревью head-web: раньше у архивной помеченной записи секции
                  // не было вовсе — «Вернуть в культуры» пропадало без единого
                  // слова, и понять, почему, было нельзя. Кнопку тут не рисуем
                  // (пометка на архивной записи ничего не решает), но говорим,
                  // в каком порядке действовать: «Вернуть из архива» — ниже.
                  <p className="text-xs text-muted-foreground">
                    Запись в архиве — сначала верните её из архива (кнопка ниже),
                    и тогда пометку можно будет снять.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {item.ref_id
                        ? 'Вернуть в культуры можно — запись снова встанет на свой эталон.'
                        : 'Вернуть в культуры можно — эталона у записи нет, поэтому она уедет в разбор («Интеграции и импорт»), где культура и выбирается.'}
                      {used > 0 &&
                        ` ${used} ${plural(used, 'посев', 'посева', 'посевов')} при этом остаются на месте.`}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="self-start"
                      disabled={busy}
                      onClick={() =>
                        revertNotACrop.mutate(item.id, {
                          onSuccess: () => {
                            toast.success('Пометка снята — запись снова культура');
                            onOpenChange(false);
                          },
                          onError: (e) =>
                            toast.error(errMsg(e, 'Не удалось снять пометку')),
                        })
                      }
                    >
                      {revertNotACrop.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Вернуть в культуры
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  «Передано в аренду», «Пастбище», «АМП» — это состояние поля,
                  угодье или смесь, а не культура. Уточните, что именно, и запись
                  перестанет считаться культурой. Посевы при этом сохраняются.
                </p>
                {/* Зеркало границы из диалога: обещание и его предел человек
                    должен увидеть ДО того, как откроет диалог. */}
                <p className="text-xs text-muted-foreground">{SUMMARIES_LIMIT_TEXT}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start"
                  disabled={busy}
                  onClick={() => setNotACropOpen(true)}
                >
                  Это не культура…
                </Button>
              </>
            )}
          </div>
        )}

        {/* Как используется — числа без ссылок */}
        <div className="flex flex-col gap-2">
          <Label>Как используется</Label>
          <p className="text-sm text-muted-foreground">
            Посевов {used}
            {growing > 0 && ` · растёт сейчас ${growing}`}
            {planned > 0 && ` · в планах ${planned}`}
          </p>
        </div>

        {/* Архив — С-4 / С-5 */}
        {planned > 0 && !archived && (
          <Alert>
            <AlertTitle>Заархивировать нельзя</AlertTitle>
            <AlertDescription>
              В планах стоит {planned} посевов этой культуры. Сначала отмените их
              или замените культуру в плане — иначе архивация молча сломает уже
              сделанное планирование.
            </AlertDescription>
          </Alert>
        )}
        {growing > 0 && planned === 0 && !archived && (
          <Alert>
            <AlertTitle>Архивация будет отложенной</AlertTitle>
            <AlertDescription>
              Запись сразу пропадёт из выбора при новом посеве, а {growing}{' '}
              растущих доработают до уборки как обычно.
            </AlertDescription>
          </Alert>
        )}

        {archived && item.merged_into_name && (
          <Alert>
            <AlertTitle>Эта запись слита в «{item.merged_into_name}»</AlertTitle>
            <AlertDescription>
              Вернуть её из архива можно, но посевы останутся на «
              {item.merged_into_name}»: слияние их уже перепривязало. Вернётся
              пустая запись — с прежними названием, цветом и областью видимости.
            </AlertDescription>
          </Alert>
        )}

        <div className="mt-auto flex flex-wrap justify-between gap-2">
          {archived ? (
            <Button
              variant="outline"
              disabled={busy}
              title={
                item.merged_into_name
                  ? `Посевы остались на «${item.merged_into_name}» — восстановление их не вернёт`
                  : undefined
              }
              onClick={() =>
                restore.mutate(item.id, {
                  onSuccess: () => {
                    toast.success('Запись вернулась в работу');
                    onOpenChange(false);
                  },
                  onError: (e) =>
                    toast.error(errMsg(e, 'Не удалось восстановить')),
                })
              }
            >
              Вернуть из архива
            </Button>
          ) : (
            <Button
              variant="outline"
              disabled={busy || planned > 0}
              onClick={() =>
                archive.mutate(item.id, {
                  onSuccess: () => {
                    toast.success('Запись в архиве');
                    onOpenChange(false);
                  },
                  onError: (e) =>
                    toast.error(errMsg(e, 'Не удалось заархивировать')),
                })
              }
            >
              В архив
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            {/* Пустой набор областей = «видна нигде». Сервер такое отвергает
                422-й, но объяснять это тостом после клика — плохой UX:
                держим кнопку до тех пор, пока выбор не осмыслен. */}
            <Button disabled={busy || !dirty || scopes.length === 0} onClick={() => save()}>
              {patch.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Сохранить
            </Button>
          </div>
        </div>
        <NotACropDialog
          item={item}
          open={notACropOpen}
          onOpenChange={setNotACropOpen}
          /* Сохранили — карточка уже про другую сущность; держать её открытой
             значит показывать устаревший эталон. «Отмена» карточку не трогает. */
          onSaved={() => onOpenChange(false)}
        />
      </SheetContent>
    </Sheet>
  );
}

export default CatalogRecordSheet;
